import { FileText, Plus } from 'lucide-react'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'

import { KpiTile } from '@/components/data/KpiTile'
import { DashboardSkeleton } from '@/components/feedback/Skeleton'
import { PageHeader } from '@/components/layout/PageHeader'
import { Button } from '@/components/ui/Button'
import { Card } from '@/components/ui/Card'
import { useSession } from '@/features/auth/api/useSession'
import { useDashboard } from '@/features/dashboard/api/useDashboard'
import { knownAttention } from '@/features/dashboard/attentionKinds'
import { AttentionList } from '@/features/dashboard/components/AttentionList'
import { CounterStrip } from '@/features/dashboard/components/CounterStrip'
import { useMyTasks } from '@/features/tasks/api/useMyTasks'
import { MyTasksStrip } from '@/features/tasks/components/MyTasksStrip'
import { ErrorState } from '@/components/feedback/ErrorState'
import { toCasePayload, useCreateCase } from '@/features/cases/api/useCase'
import { CaseFormSheet } from '@/features/cases/components/CaseFormSheet'
import { useFormat } from '@/i18n/useFormat'
import { formatNumber } from '@/lib/format'
import { can, CAP } from '@/lib/can'
import type { DashboardSummary, Money } from '@/types/domain'

/** Which greeting to use. Both catalogues supply three; the boundaries live
 *  here because Russian and English divide the day differently and neither
 *  split belongs in a translation string. */
function greetingKey(hour: number): 'morning' | 'afternoon' | 'evening' {
  if (hour < 12) return 'morning'
  if (hour < 17) return 'afternoon'
  return 'evening'
}

/* The three money tiles, in the order the mockup places them.
 *
 * `profit` rather than `forecastProfit`: FR-51 AC 2 makes the tile one figure
 * over two bases — a financially closed case contributes what it finally earned
 * and every other case what it is forecast to earn — and the server composes
 * that as `counters.profit`. `forecastProfit` is still on the response as that
 * figure's open-cases component; it is not a tile.
 *
 * Rendered from a list rather than written out three times because a tile stops
 * being pending individually, which is a change this list makes obvious. */
const MONEY_TILES = ['turnover', 'profit', 'toCollect'] as const

type MoneyTileKey = (typeof MONEY_TILES)[number]

/** The story that unblocks a tile, or undefined if the server sent the figure.
 *
 *  Reading the reason off the RESPONSE rather than hard-coding it here: the
 *  server owns what it could not compute, and a copy of that list in the client
 *  is a copy that goes stale the moment a story lands. */
function storyFor(
  pending: DashboardSummary['pending'],
  key: MoneyTileKey,
): string | undefined {
  return pending.find((p) => p.key === key)?.story
}

/** Where a tile's figure drills through to (UX-DR-10).
 *
 *  EMPTY TODAY, AND THAT IS THE FINDING RATHER THAN A GAP IN IT. UX-DR-10 asks
 *  for every figure to open the filtered list behind it, and it is explicit
 *  about the harder half: "the tile and its destination list are computed from
 *  the same predicate and the same rate basis, so the list always sums to the
 *  figure that was clicked". A link that lands on a list computed differently is
 *  worse than no link — it teaches the founder that the figures disagree, which
 *  is the doubt this whole surface exists to remove.
 *
 *  No destination sums to its figure yet, and each is one specific missing
 *  thing:
 *
 *    · `profit` has no list at all. There is no per-case profit view to land on.
 *    · `toCollect` and `toPay` would land on Finance → Balances or Aging. Those
 *      are real reads now (`internal/finance`, since 2026-08-21 — no longer
 *      `internal/showcase` fixtures), but they are ranged by the page's date
 *      control and grouped by counterparty, and nothing proves the rows sum to
 *      the tile's all-open, booked figure. Until a test pins that, the link
 *      would invite exactly the comparison above. «Мои задачи»'s «К оплате»
 *      line does link to the balances (S-2b.2, B1), and may: it is a "go and
 *      act" link that promises no sum, not this map's drill-through.
 *    · The active-case COUNTER would land on `/cases`, which shows every case
 *      the caller may see while the counter counts only IN PROCESS ones. The
 *      case list does not read a status from the URL, so the link cannot narrow
 *      it.
 *
 *  The affordance itself is wired: `KpiTile` takes a `link` and renders it, and
 *  the attention rows already drill through to a single record — where the
 *  destination is the record itself and therefore cannot disagree. Populating
 *  this map is the whole of turning the tiles on, once a destination can sum. */
const TILE_HREFS: Partial<Record<MoneyTileKey, string>> = {}

/** The figure a tile renders, or undefined when the response carries none.
 *
 *  A switch rather than `counters[key]`: a tile key and a `counters` field are
 *  not the same namespace (`profit` the tile is not `forecastProfit` the
 *  field), and a key added to MONEY_TILES without a field fails to compile here
 *  instead of rendering nothing. `turnover` reads its field since Story B7; it
 *  is absent, never zero, when the server's read failed. */
function figureFor(
  counters: DashboardSummary['counters'],
  key: MoneyTileKey,
): Money | undefined {
  switch (key) {
    case 'profit':
      return counters.profit
    case 'toCollect':
      return counters.toCollect
    case 'turnover':
      return counters.turnover
  }
}

/** A tile whose figure the server could not compute yet.
 *
 *  It states the absence rather than rendering an empty card, for the reason
 *  UX-DR-17 gives about empty states generally: a blank box is read as a
 *  loading failure, and a zero is read as data. The `story` the server sends is
 *  the presence flag only: the reader is told the figure is not calculated yet
 *  and is never handed a backlog number (QW-4). */
function PendingTile({ label, story }: { label: string; story?: string }) {
  const { t } = useTranslation()
  if (!story) return null
  return (
    <Card className="p-[18px]">
      <span className="text-text-muted text-[13px] leading-tight font-normal">
        {label}
      </span>
      <p className="text-text-muted mt-3 text-[13px] leading-[1.5] font-light">
        {t('dashboard.pending.description')}
      </p>
    </Card>
  )
}

/** A tile whose figure the server DID compute.
 *
 *  Renders nothing when the amount is absent, which is not the same as
 *  rendering a zero: the server omits a figure it could not compute, and the
 *  tile it would have filled is left to PendingTile — or, when the server named
 *  no story either, to nothing at all. An empty slot is honest; a zero is a
 *  number the founder would act on.
 *
 *  BUILT ON KpiTile rather than on a Card of its own. That is the fix for the
 *  orphaned-component finding and it is not just tidiness: `KpiTile` already
 *  carries the `link` UX-DR-10 needs, already renders it in the header where the
 *  mockup puts it, and is already the tile the quotes surface uses
 *  (PipelineStatistics) — so the dashboard and the pipeline now agree on what a
 *  KPI looks like instead of each having a private one. `KpiCard`, the wrapper
 *  that used to sit between them, is deleted: it composed a generic `KpiTile`
 *  domain object the server does not send and, per UX-DR-10's second clause,
 *  must not — a generic tile list is precisely how a figure and its destination
 *  come to be computed from different predicates. */
function MoneyTile({
  label,
  amount,
  href,
  hrefLabel,
}: {
  label: string
  amount?: Money
  href?: string
  hrefLabel: string
}) {
  if (!amount) return null
  return (
    <KpiTile
      label={label}
      value={formatNumber(amount.amount, 2)}
      unit={amount.currency}
      link={href ? { to: href, label: hrefLabel } : undefined}
    />
  )
}

/* The founder dashboard — the COMFORTABLE regime. The founder reads five
 * numbers over morning coffee and needs them to land in seconds, so this
 * surface is spacious where the case list is dense. Both are two settings of
 * one system, not two systems. */

export function DashboardPage() {
  const { t } = useTranslation()
  const format = useFormat()
  const navigate = useNavigate()
  const [creating, setCreating] = useState<'case' | null>(null)
  const { data: user } = useSession()
  const createCase = useCreateCase()
  /* No range selector. Turnover — the one range-sensitive figure — is served
     for the current calendar month only (owner decision O1, Story B7), and its
     label says so; the server reads no range (see useDashboard). The control
     returns with a range the server honours, and with the shared date-range
     component Story 6.3 introduces — a picker that visibly changes nothing is
     worse than no picker. */
  const { data, isPending, isError, error, refetch } = useDashboard()
  /* «Мои задачи», for the strip above the counters (S-2b.2). Read once here so
     the strip and the subtitle count the same answer. */
  const tasks = useMyTasks()

  const firstName = user?.name.split(' ')[0] ?? ''
  const now = new Date()

  /* What waits on the reader: the «Требует решения» rows this build can show
     (`knownAttention` — a rolled-back server's approval row is skipped by the
     list, so it is not counted either) plus every task the strip holds. Only
     once BOTH reads have answered: half a count is a wrong count. */
  const waiting =
    data && tasks.data
      ? knownAttention(data.attention).length +
        tasks.data.sections.reduce((sum, section) => sum + section.count, 0)
      : undefined

  /* The eyebrow states when the SERVER read the figures, not when this browser
     rendered them (NFR-2, UX-DR-16). The distinction is the whole criterion: a
     laptop resumed from sleep shows a cached response, and a browser-stamped
     time would date it "now" — the stale tab mistaken for a live one.

     The day and the hour are both read from that one instant in the viewer's
     zone (`instantDateLong`), as «Мои задачи» does: slicing the UTC day off
     `readAt` dated a 02:30 Ashgabat read with yesterday's day every night until
     05:00. Before the first response lands there is no server time to state,
     so the eyebrow falls back to today's date alone rather than inventing a
     time — through the same formatter, so it names the same local day. */
  /* The two create acts, each on the key the server checks for it (#128,
     N-07) — Finance holds neither, and was offered both forms only to be
     refused on submit. No actions slot at all when neither is held. */
  const mayCreateQuote = can(user, CAP.quoteCreate)
  const mayCreateCase = can(user, CAP.caseCreate)

  const header = (
    <PageHeader
      eyebrow={
        data
          ? t('dashboard.eyebrow', {
              date: format.instantDateLong(data.readAt),
              time: format.time(data.readAt),
            })
          : format.instantDateLong(now.toISOString())
      }
      title={t(`dashboard.greeting.${greetingKey(now.getHours())}`, {
        name: firstName,
      })}
      subtitle={waiting === undefined ? undefined : t('dashboard.subtitle', { count: waiting })}
      actions={
        mayCreateQuote || mayCreateCase ? (
          <>
            {mayCreateQuote ? (
              <Button
                variant="secondary"
                iconLeft={FileText}
                onClick={() => navigate('/quotes/new')}
              >
                {t('quotes.actions.newQuote')}
              </Button>
            ) : null}
            {mayCreateCase ? (
              <Button iconLeft={Plus} onClick={() => setCreating('case')}>
                {t('cases.actions.newCase')}
              </Button>
            ) : null}
          </>
        ) : undefined
      }
    />
  )

  /* The dashboard's create actions. A new QUOTE is a page, not a sheet — the
     server mints its number, so nothing can hang off it until it exists, and
     /quotes/new lands the user on the record itself — so only the case sheet is
     mounted here. It sits outside the loading and error branches below,
     so a create started from the greeting survives a background refetch.

     The sheet closes from its own onSuccess, never from onSubmit: closing on
     submit would discard what was typed the moment the server refused it, and
     the refusal would land on a sheet that was already gone (UX-DR-18). */
  const sheets = (
    <>
      {mayCreateCase ? (
        <CaseFormSheet
          open={creating === 'case'}
          onClose={() => setCreating(null)}
          pending={createCase.isPending}
          onSubmit={(draft) =>
            createCase.mutate(toCasePayload(draft), {
              onSuccess: () => {
                setCreating(null)
                /* To the LIST, not to the case. The detail route is still served
                   by the showcase fixtures and 404s for every real id, so sending
                   the user onto the case they just opened showed "запись
                   недоступна" over a save that had in fact succeeded. The list is
                   real and the new case is in it. Becomes `/cases/${record.id}`
                   when the AD-23 read composer serves GET /cases/:id. */
                navigate('/cases')
              },
            })
          }
        />
      ) : null}

      {/* A refused create renders beside the greeting rather than inside the
          sheet, which stays open still holding the draft: nothing typed is lost
          to a failed save (UX-DR-18). Part of `sheets` rather than a sibling so
          the loading and error branches below cannot render the sheets without
          the one surface that says why a save did not take. */}
      {createCase.error ? (
        <ErrorState error={createCase.error} onRetry={() => createCase.reset()} />
      ) : null}
    </>
  )

  /* The reader's tasks, between the greeting and the counters, in every branch:
     the two reads fail independently, and a dashboard still loading or refused
     is no reason to hide a rate approval waiting on its reader. */
  const strip = <MyTasksStrip tasks={tasks} />

  if (isPending) {
    return (
      <>
        {header}
        {strip}
        <DashboardSkeleton />
        {sheets}
      </>
    )
  }

  if (isError) {
    return (
      <>
        {header}
        {strip}
        <ErrorState error={error} onRetry={() => void refetch()} />
        {sheets}
      </>
    )
  }

  // Money tiles follow the receivables read, exactly as the Finance nav item
  // does — the capability, not the role's name, so a custom role granted it
  // sees them too. The server withholds the figures either way; this keeps the
  // layout honest rather than rendering three empty cards.
  const showMoney = can(user, CAP.financeReceivablesRead)

  return (
    <>
      {header}

      {strip}

      <CounterStrip counters={data.counters} pending={data.pending} />

      {showMoney ? (
        <div className="grid items-start gap-3.5 lg:grid-cols-3">
          {MONEY_TILES.map((key) => {
            const story = storyFor(data.pending, key)
            /* The branch is on the DATA, never on the key: which tile carries a
               figure is the response's business, and a client that hard-coded
               "profit is real now" would have to be edited again the next time a
               story lands. Since Story B7 the server declares nothing pending,
               and a rolled-back one that names a key again is still obeyed. */
            if (story) {
              return (
                <PendingTile
                  key={key}
                  label={t(`dashboard.tiles.${key}`)}
                  story={story}
                />
              )
            }
            return (
              <MoneyTile
                key={key}
                label={t(`dashboard.tiles.${key}`)}
                amount={figureFor(data.counters, key)}
                href={TILE_HREFS[key]}
                hrefLabel={t('dashboard.tiles.drillThrough')}
              />
            )
          })}
        </div>
      ) : null}

      <AttentionList items={data.attention} stuckDays={data.stuckDays} />

      {sheets}
    </>
  )
}
